import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Column, Row } from 'wrappers/FlexTable';
import CustomCheckBox from 'components/CustomCheckBox';
import { truncate } from 'helpers/util';
import LoadingSpinner from 'components/LoadingSpinner';

const AMENITY_GROUPS = {
  basics: 'Basics',
  buildings: 'Building Features',
  kitchens: 'Kitchen Features',
  luxuries: 'Luxury Features',
  outdoors: 'Outdoor Features'
};

const AmenityFilter = props => {
  const {
    selected,
    onChange,
    options,
  } = props;

  const [searchQuery, setSearchQuery] = useState('');

  const groupedAmenities = useMemo(() => {
    const groups = {};
    const searchLower = searchQuery.toLowerCase();

    // Initialize all groups
    Object.keys(AMENITY_GROUPS).forEach(group => {
      groups[group] = [];
    });

    options.forEach(option => {
      // Use group if available, otherwise fall back to type
      const group = option.group || option.type;
      if (!group) return; // Skip if no group or type
      
      if (!searchQuery || option.name.toLowerCase().includes(searchLower)) {
        if (groups[group]) {
          groups[group].push(option);
        }
      }
    });

    return groups;
  }, [options, searchQuery]);

  const handleClick = option => {
    const tempValue = [...selected];

    if (tempValue.includes(option)) {
      tempValue.splice(tempValue.indexOf(option), 1);
      onChange(tempValue);
    } else {
      tempValue.push(option);
      onChange(tempValue);
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.currentTarget.click();
    }
  };

  return (
    <Column>
      <div style={{
        margin: '24px 0px',
        fontWeight: '500',
        fontSize: '14px',
        lineHeight: '19px',
        color: '#fff',
      }}
      >
        Amenities
      </div>
      <div style={{ 
        marginBottom: '16px',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#666',
          fontSize: '14px',
          pointerEvents: 'none',
        }}>
          🔍
        </div>
        <input
          type="text"
          placeholder="Search amenities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px 8px 36px',
            borderRadius: '4px',
            border: '1px solid #333',
            backgroundColor: '#1a1a1a',
            color: '#fff',
            fontSize: '14px',
          }}
        />
      </div>
      <div className="d-flex">
        <Column
          style={{
            flexWrap: 'wrap',
            paddingLeft: '0px',
            border: '0px',
            maxHeight: 'none',
            overflowY: 'visible',
          }}
          className="region-hood-content amenity-list"
        >
          {options.length ? (
            Object.entries(AMENITY_GROUPS).map(([groupKey, groupName]) => {
              const amenities = groupedAmenities[groupKey] || [];
              return (
                <div key={groupKey} style={{ marginBottom: '16px' }}>
                  <div style={{
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    paddingLeft: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {groupName}
                  </div>
                  {amenities.length > 0 ? (
                    <Row style={{ flexWrap: 'wrap', display: 'flex' }}>
                      {amenities.map(option => (
                        <Column
                          key={`amenity-${option.value}`}
                          className="individual-amenity"
                          style={{
                            display: 'flex',
                            paddingTop: '2px',
                            paddingBottom: '10px',
                            paddingRight: '0px',
                            paddingLeft: '0px',
                            width: '50%',
                            alignItems: 'flex-start',
                          }}
                          onClick={() => handleClick(option.value)}
                          onKeyPress={handleKeyPress}
                        >
                          <CustomCheckBox
                            boxClass="matrix-checkbox"
                            boxcheckedClass="matrix-checkbox-checked"
                            checkmarkClass="checkmark"
                            dataName={option.value}
                            checked={selected.includes(option.value)}
                            type="checkbox"
                            name={option.value}
                            value={option.value}
                          >
                            <Column
                              style={{
                                color: '#fff',
                                fontSize: '13px',
                                fontWeight: '500',
                                paddingLeft: '10px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                textAlign: 'left',
                                lineHeight: '1.4',
                              }}
                            >
                              {truncate(option.name, 23)}
                            </Column>
                          </CustomCheckBox>
                        </Column>
                      ))}
                    </Row>
                  ) : (
                    <div style={{ 
                      color: '#666', 
                      fontSize: '13px', 
                      paddingLeft: '4px',
                      fontStyle: 'italic',
                    }}>
                      No matching amenities
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <LoadingSpinner />
          )}
        </Column>
      </div>
    </Column>
  );
};

AmenityFilter.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    group: PropTypes.string,
  })).isRequired,
};

export default AmenityFilter;
